/** Explicit observation of native runtime behavior; never starts a render loop. */
import type { SceneObject, TraceScene } from 'trace-viewer/scene';
import { Properties, Timeline, BehaviorGraph, Text, Pages } from 'trace-viewer/behaviors';
import { NullEngine } from '@babylonjs/core/Engines/nullEngine';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { getEntries } from './console-buffer';
import type { BindingDiagnostic } from 'trace-viewer/behaviors';

export function inspectBindings(objects: SceneObject[], limit = 100): BindingDiagnostic[] {
    const diagnostics: BindingDiagnostic[] = [];
    for (const object of objects) for (const behavior of object.getBehaviors()) {
        diagnostics.push(...behavior.getBindingDiagnostics(Math.max(0, limit - diagnostics.length)));
        if (diagnostics.length >= limit) return diagnostics;
    }
    return diagnostics;
}

export interface StaticReadout { object: string; text: string; shows: string }

/**
 * Text that prints a number equal to some numeric property's current value
 * but is not bound to anything: a readout that looks right now and goes stale
 * the moment the property changes (s3-sol's fixed "10 kg" tray label beside
 * `trayMass = 10`). Constant labels ("1 kg" on a weight) match no property and
 * are not reported, nor is text on a Pages page: the page is what changes, so
 * its numbers (a step caption's "1", a "2x" count) are its own. A hint to
 * check, not a verdict.
 */
export function findStaticReadouts(trace: TraceScene, limit = 10): StaticReadout[] {
    const values: { owner: string; name: string; value: number }[] = [];
    for (const object of trace.getSceneObjects()) {
        const properties = object.getBehavior(Properties);
        if (!properties) continue;
        for (const name of properties.getPropertyNames()) {
            const value = properties.getValue(name);
            if (typeof value === 'number' && Number.isFinite(value)) values.push({ owner: object.name, name, value });
        }
    }
    if (!values.length) return [];
    const found: StaticReadout[] = [];
    for (const object of trace.getSceneObjects()) {
        const authored = object.getBehavior(Text)?.getData().text;
        if (typeof authored !== 'string' || authored.includes('{') || onPage(object)) continue;
        const numbers = [...authored.matchAll(/-?\d+(?:\.\d+)?/g)].map(match => Number(match[0]));
        const match = values.find(entry => numbers.some(number => Math.abs(number - entry.value) < 1e-9));
        if (!match) continue;
        found.push({ object: object.getPath(), text: authored.slice(0, 60), shows: `${match.owner}.${match.name} = ${match.value}` });
        if (found.length >= limit) break;
    }
    return found;
}

/** Whether `object` is inside one of a Pages container's page nodes; its shared children, which are not pages, don't count. */
function onPage(object: SceneObject): boolean {
    for (let at = object, parent = at.getParent(); parent; at = parent, parent = parent.getParent()) {
        if (parent.getBehavior(Pages)?.getPageNodes().includes(at.transform)) return true;
    }
    return false;
}

const clocks = new WeakMap<TraceScene, { nowMs: number }>();

/** Keep native time.elapsed/trace/time on the same explicit headless clock. */
export function enableRuntimeStepping(trace: TraceScene) {
    if (!(trace.getBabylonScene().getEngine() instanceof NullEngine)) throw new Error('Fixed stepping requires NullEngine');
    let clock = clocks.get(trace);
    if (!clock) {
        clock = { nowMs: 0 };
        const source = clock;
        trace.getSystemValues().setClock(() => source.nowMs);
        clocks.set(trace, clock);
    }
    return clock;
}

/**
 * How far an object's local +X and +Z ends sit above the world horizontal, in
 * degrees (negative = that end is lower). Read from the world matrix, so parent
 * rotations count and no quaternion arithmetic is left to the reader: a beam
 * that should dip toward its heavier +X side must report a negative `x`.
 */
function slopeDeg(transform: SceneObject['transform']): { x: number; z: number } {
    const world = transform.getWorldMatrix();
    const rise = (axis: Vector3) => {
        const direction = Vector3.TransformNormal(axis, world).normalize();
        return Math.asin(Math.max(-1, Math.min(1, direction.y))) * 180 / Math.PI;
    };
    return { x: rise(Vector3.Right()), z: rise(Vector3.Forward()) };
}

export function runtimeSnapshot(objects: SceneObject[]) {
    return objects.map(object => {
        const transform = object.transform;
        transform.computeWorldMatrix(true);
        const properties = object.getBehavior(Properties);
        const timeline = object.getBehavior(Timeline);
        const pages = object.getBehavior(Pages);
        return {
            path: object.getPath(),
            transform: {
                position: transform.position.asArray(), worldPosition: transform.getAbsolutePosition().asArray(),
                rotation: transform.rotationQuaternion?.asArray() ?? transform.rotation.asArray(), scale: transform.scaling.asArray(),
                slopeDeg: slopeDeg(transform),
            },
            properties: properties ? structuredClone(Object.fromEntries(properties.getPropertyNames().map(name => [name, properties.getValue(name)]))) : {},
            timeline: timeline ? { time: timeline.getTime(), playing: timeline.isPlaying() } : undefined,
            page: pages ? { index: pages.getActivePageIndex(), count: pages.getPageCount() } : undefined,
        };
    });
}

/** NullEngine needs frames AND a delta; live viewers keep their own clocks. */
export async function probeRuntime(trace: TraceScene, objects: SceneObject[], durationMs: number) {
    if (!Number.isFinite(durationMs) || durationMs < 1 || durationMs > 10000) throw new Error('duration_ms must be between 1 and 10000');
    if (!trace.isPlayMode()) throw new Error('Enter play mode with set_play_mode before probe_runtime');
    if (!objects.length || objects.length > 32) throw new Error('Choose between 1 and 32 objects');
    const scene = trace.getBabylonScene(), engine = scene.getEngine();
    const before = runtimeSnapshot(objects);
    const bindingDiagnosticsBefore = inspectBindings(objects);
    const graphDiagnostics = objects.flatMap(object => (object.getBehavior(BehaviorGraph)?.getRuntimeDiagnostics() ?? []).map(d => ({ object: object.getPath(), ...d })));
    const existingLogs = new Set(getEntries());
    const moving = new Set<string>(), changed = new Set<string>();
    let previous = before, frames = 0, elapsedMs = 0;
    const sample = () => {
        const current = runtimeSnapshot(objects);
        for (let i = 0; i < current.length; i++) {
            if (JSON.stringify(current[i].transform) !== JSON.stringify(previous[i].transform)) moving.add(current[i].path);
            if (JSON.stringify(current[i]) !== JSON.stringify(previous[i])) changed.add(current[i].path);
        }
        previous = current;
        frames++;
    };
    const deterministic = engine instanceof NullEngine;
    if (deterministic) {
        const clock = enableRuntimeStepping(trace);
        const descriptor = Object.getOwnPropertyDescriptor(engine, 'getDeltaTime');
        let delta = 0;
        Object.defineProperty(engine, 'getDeltaTime', { configurable: true, writable: true, value: () => delta });
        try {
            const frameCount = Math.ceil(durationMs / (1000 / 60));
            for (let i = 0; i < frameCount; i++) {
                delta = i === frameCount - 1 ? durationMs - elapsedMs : 1000 / 60;
                clock.nowMs += delta;
                scene.render();
                elapsedMs += delta;
                sample();
            }
        } finally {
            if (descriptor) Object.defineProperty(engine, 'getDeltaTime', descriptor);
            else Reflect.deleteProperty(engine, 'getDeltaTime');
        }
    } else {
        await new Promise<void>((resolve, reject) => {
            const timer = setTimeout(() => {
                observer?.remove();
                reject(new Error(`Viewer did not render ${durationMs} ms of runtime; observed ${frames} frames / ${elapsedMs} ms`));
            }, durationMs + 5000);
            const observer = scene.onAfterRenderObservable.add(() => {
                try {
                    elapsedMs += engine.getDeltaTime();
                    sample();
                    if (elapsedMs >= durationMs) { clearTimeout(timer); observer?.remove(); resolve(); }
                } catch (error) { clearTimeout(timer); observer?.remove(); reject(error); }
            });
        });
    }
    return {
        mode: deterministic ? 'fixed-step' : 'live-frames', requestedMs: durationMs, elapsedMs, frames,
        graphDiagnostics, before, after: previous, movingObjects: [...moving], changedObjects: [...changed],
        bindingDiagnosticsBefore, bindingDiagnosticsAfter: inspectBindings(objects),
        logs: getEntries().filter(entry => !existingLogs.has(entry) && (entry.type === 'warn' || entry.type === 'error')).slice(-30),
        limitations: [
            'Observes frame-driven native behavior and time.elapsed; wall-clock timers and scripts are not deterministically simulated.',
            'Console entries cover this observation window and can include other concurrent scenes.',
            'Motion alone does not prove mechanical correctness or real pointer picking.',
            'Binding diagnostics inspect authored fields, including inactive states, on requested objects only; capped at 100 findings.',
        ],
    };
}

export type RuntimeProbeReport = Awaited<ReturnType<typeof probeRuntime>>;
